import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import SearchFilter from "./SearchFilter";
import "./AppTable.css";
import PaginationPannel from "./PaginationPannel";
import ThreeColumnRow from "../Layouts/ThreeColumnRow";
import { scrollToTop } from "../../util/AppUtils";
import RecentItems from "./RecentItems";
import useTableShadow from "./useTableShadow";

const AppTable = React.forwardRef(
  (
    {
      columnsData = [],
      showSearchInput = true,
      searchInputPosition = "center",
      contentBeforeSearchFilter,
      contentAfterSearchFilter,
      searchBarLeftContent,
      searchBarRightContent,
      customeTableHeight = "",
      paginate = false,
      pagination = {
        limit: [0, () => { }],
        offset: [0, () => { }],
        pagination: {},
        paginationPosition: {},
        paginationShadow: false,
        maxVsbleButtons: 10,
        showRecentItems: false,
        disabled: false,
      },
      onFilterSortHandler = (searchQuery) => { },
      children,
      setLocalSortConfig,
      extrapadding = "17px",
      contentAfterHeader = "",
      usingInPopup = false,
      reduxSlice = null, // Add this prop to specify which Redux slice to update
      enableLocalStoragePersistence = false, // Add this prop to enable localStorage persistence
      showScroll = false,
      disableSearchFilters = false, // When true, hides the column filter icon and dropdown in SearchFilter
      hideScrollbar = false, // When true, hides the vertical scrollbar on the table container (e.g. for audit preview)
    },
    ref
  ) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedColumns, setSelectedColumns] = useState(
      columnsData
        .filter((c) => c.filter)
        .reduce((acc, element) => ({ ...acc, [element.columnKey || element.field]: true }), {})
    );
    const [showRecentItems, setShowRecentItems] = useState();

    const appTableContaier = useRef(null);

    const showActionShadow = useTableShadow(appTableContaier);

    useEffect(() => {
      const filterableColumns = columnsData.filter((c) => c.filter);
      if (filterableColumns.length === 0) {
        return;
      }

      setSelectedColumns((prev = {}) => {
        const expectedKeys = filterableColumns.map((col) => col.columnKey || col.field);
        const prevKeys = Object.keys(prev);
        const hasSameKeys =
          prevKeys.length === expectedKeys.length &&
          expectedKeys.every((key) => prevKeys.includes(key));

        if (hasSameKeys) {
          return prev;
        }

        const updated = {};
        filterableColumns.forEach((col) => {
          const key = col.columnKey || col.field;
          updated[key] = prev[key] ?? true;
        });

        return updated;
      });
    }, [columnsData]);

    const [searchType, setSearchType] = useState("all_columns");

    // Enhanced setLimit with localStorage persistence (only when enabled)
    const [limit, setLimit] = useState(() => {
      if (enableLocalStoragePersistence) {
        const savedLimit = localStorage.getItem("appTableLimit");

        return savedLimit ? parseInt(savedLimit) : pagination.limit?.[0] || 10;
      }
      return pagination.limit?.[0] || 10;
    });

    // Enhanced setOffset with localStorage persistence (only when enabled)
    const [offset, setOffset] = useState(() => {
      if (enableLocalStoragePersistence) {
        const savedOffset = localStorage.getItem("appTableOffset");

        return savedOffset
          ? parseInt(savedOffset)
          : pagination.offset?.[0] || 0;
      }
      return pagination.offset?.[0] || 0;
    });

    // Wrapper functions to persist changes to localStorage and Redux (only when enabled)
    const setLimitWithPersistence = (newLimit) => {
      if (enableLocalStoragePersistence) {
        localStorage.setItem("appTableLimit", newLimit.toString());
      }
      setLimit(newLimit);
      if (pagination.limit?.[1]) {
        pagination.limit[1](newLimit); // Call the original setter
      }
      // Update Redux state if reduxSlice is provided
      if (reduxSlice && reduxSlice.setLimit) {
        reduxSlice.setLimit(newLimit);
      } else {
      }
    };

    const setOffsetWithPersistence = (newOffset) => {
      if (enableLocalStoragePersistence) {
        localStorage.setItem("appTableOffset", newOffset.toString());
      }
      setOffset(newOffset);
      if (pagination.offset?.[1]) {
        pagination.offset[1](newOffset); // Call the original setter
      }
      // Update Redux state if reduxSlice is provided
      if (reduxSlice && reduxSlice.setOffset) {
        reduxSlice.setOffset(newOffset);
      } else {
      }
    };

    const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: null,
    });
    const [goto, setGoto] = useState();

    const handleSort = (key) => {
      let direction = "";
      if (sortConfig.key !== key) {
        direction = "ASC";
      } else if (
        sortConfig.direction === null ||
        sortConfig.direction === "" ||
        sortConfig.direction === "DEF"
      ) {
        direction = "ASC";
      } else if (sortConfig.direction === "ASC") {
        direction = "DESC";
      } else if (sortConfig.direction === "DESC") {
        direction = "DEF";
      }
      setSortConfig({ key, direction });
      if (setLocalSortConfig) setLocalSortConfig({ key, direction });
    };

    const prevSelectedColumnsRef = useRef(selectedColumns);
    const prevSearchQueryRef = useRef(searchQuery);

    useEffect(() => {
      let reqPayload = `limit=${limit || 100000}&offset=${offset || 0}`;
      if (selectedColumns && searchQuery && searchQuery.trim().length > 0) {
        let searchType = [];
        for (let key in selectedColumns) {
          if (selectedColumns[key] === true) {
            // Map columnKey back to field name for API
            const column = columnsData.find((col) => (col.columnKey || col.field) === key);
            const fieldName = column ? column.field : key;
            searchType.push(fieldName);
          }
        }
        if (searchType.length > 0) {
          reqPayload += `&search_type=${searchType.join(
            ","
          )}&search=${searchQuery}`;
        }
      }
      if (sortConfig) {
        if (sortConfig.key !== null && sortConfig.direction !== null)
          if (
            sortConfig.key.length > 0 &&
            sortConfig.direction.length > 0 &&
            sortConfig.direction !== "DEF"
          ) {
            let key = sortConfig.key;
            if (sortConfig.key === "type") {
              key = "name";
            }
            reqPayload += `&sort_by=${key}&sort_order=${sortConfig.direction}`;
          }
      }
      if (showRecentItems) {
        const urlParams = new URLSearchParams(reqPayload);
        urlParams.delete("search_type");
        urlParams.delete("search");
        reqPayload = urlParams.toString() + `&recent_item_sort=true`;
      }

      // Use a longer debounce when checkboxes (selectedColumns) or searchQuery changed
      // to avoid unnecessary immediate API calls on each checkbox toggle.
      // Pagination, sort, and other changes fire quickly (0ms).
      const columnsChanged = prevSelectedColumnsRef.current !== selectedColumns;
      const searchChanged = prevSearchQueryRef.current !== searchQuery;
      prevSelectedColumnsRef.current = selectedColumns;
      prevSearchQueryRef.current = searchQuery;

      const delay = columnsChanged || searchChanged ? 1000 : 0;

      const timeout = setTimeout(() => {
        onFilterSortHandler(reqPayload);
      }, delay);

      return () => {
        clearTimeout(timeout);
      };
    }, [
      limit,
      offset,
      sortConfig,
      pagination,
      searchQuery,
      selectedColumns,
      onFilterSortHandler,
      showRecentItems,
    ]);

    useEffect(() => {
      if (searchQuery.length > 0) {
        setOffset(0);
      }
    }, [searchQuery, setOffset]);

    useEffect(() => {
      if (paginate) {
        if (pagination?.pagination?.offset > pagination.pagination?.total) {
          setOffset(0);
        }
      }
    }, [paginate, pagination, setOffset]);

    useEffect(() => {
      if (appTableContaier.current) {
        const tableRef = {
          current: appTableContaier.current.querySelector(".APP-TABLE"),
        };
        scrollToTop(tableRef);
      }
    }, [offset]);

    // for manage recent items
    useEffect(() => {
      if (showRecentItems === undefined && pagination.showRecentItems) {
        setShowRecentItems(true);
      }
    }, [pagination, showRecentItems]);

    useEffect(() => {
      if (showRecentItems && searchQuery.length !== 0) {
        setShowRecentItems(false);
      }
    }, [searchQuery, offset, selectedColumns, showRecentItems]);

    const handleRecentItems = () => {
      setOffset(0);
      setSearchQuery("");
      setSelectedColumns((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((k) => (updated[k] = true));
        return updated;
      });
      setSearchType("all_columns");
      setShowRecentItems(true);
    };

    const searchFiler = (
      <SearchFilter
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        columnOptions={columnsData.filter((c) => c.filter)}
        searchType={searchType}
        setSearchType={setSearchType}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        disableSearchFilters={disableSearchFilters}
      />
    );

    let scrollAdjustment = {};
    if ((paginate && pagination?.pagination?.totalPages === 0)) {
      scrollAdjustment = { paddingTop: "10px", overflow: "auto" };
    }

    const hasActionColumn = columnsData.some((col) => col.isAction);

    return (
      <>
        <div style={{ padding: extrapadding }}>
          {showSearchInput && (
            <>
              {searchInputPosition === "center" && (
                <ThreeColumnRow
                  children={[
                    <div style={{ marginTop: "15px" }}>
                      {contentBeforeSearchFilter}
                    </div>,
                    <div className="search-container">{searchFiler}</div>,
                    <div style={{ marginTop: "15px" }}>
                      {contentAfterSearchFilter}
                    </div>,
                  ]}
                />
              )}

              {searchInputPosition === "left" && (
                <div className="left-right-layout">
                  <div style={{ marginLeft: "-20px" }} className="search-container">{searchFiler}</div>
                  <div>{searchBarRightContent}</div>
                </div>
              )}

              {searchInputPosition === "right" && (
                <div className="left-right-layout" style={{ marginBottom: 0 }}>
                  <div>{searchBarLeftContent}</div>
                  <div className="right-search-bar search-container">{searchFiler}</div>
                </div>
              )}
            </>
          )}

          {contentAfterHeader}

          <div className={`APP-TABLE-CONTAINER ${hideScrollbar ? "hide-table-scrollbar" : ""}`} ref={appTableContaier}>
            <div
              className={`APP-TABLE ${paginate || showScroll ? "APP-TABLE-HEIGHT" : ""} ${usingInPopup ? "APP-POPUP-TABLE" : "APP-PAGE-TABLE"
                }`}
              style={scrollAdjustment}
              ref={ref}
            >

              <table className={`${hasActionColumn ? "has-action-column" : ""} ${showActionShadow ? "show-action-shadow" : ""}`} cellSpacing={0} cellPadding={0}>
                <thead>
                  <tr>
                    {columnsData.filter((col) => !col.hidden).map((col) => {
                      const { headName, field, sort, isAction } = col;

                      return (
                        <th
                          key={col.field}
                          onClick={() => sort && handleSort(field)}
                          style={{
                            width: col.width,
                            textAlign: col.textCenter || isAction ? "center" : "left",
                          }}
                          className={`${sort ? "sortable-header " : ""} ${!sort ? "no-sort" : ""
                            } ${isAction ? "sticky-action-th" : ""}`}
                        >
                          <div
                            className="header-content"
                            style={{
                              paddingTop: "10px",
                              justifyContent: col.textCenter || isAction
                                ? "center"
                                : "flex-start",
                              width: col?.width,
                            }}
                          >
                            <span>
                              {headName}
                              {col.required && (
                                <span
                                  style={{
                                    color: "#0D6EFD",
                                    marginLeft: "2px",
                                  }}
                                >
                                  *
                                </span>
                              )}
                            </span>
                            {sort && (
                              <div
                                className="sort-icons"
                                style={{ fontSize: "8px" }}
                              >
                                <span
                                  style={{
                                    opacity:
                                      sortConfig.direction === "DESC" &&
                                      sortConfig.key === field &&
                                      0.5,
                                  }}
                                >
                                  {"▲"}
                                </span>
                                {"\n"}
                                <span
                                  style={{
                                    opacity:
                                      sortConfig.direction === "ASC" &&
                                      sortConfig.key === field &&
                                      0.5,
                                  }}
                                >
                                  {"▼"}
                                </span>
                              </div>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>{children}</tbody>
              </table>
            </div>
          </div>
        </div>

        {paginate && (
          <PaginationPannel
            pagination={pagination.pagination}
            setOffset={setOffsetWithPersistence}
            offset={offset}
            setLimit={setLimitWithPersistence}
            limit={limit}
            setGoto={setGoto}
            goto={goto}
            paginationPosition={pagination.paginationPosition}
            paginationShadow={pagination.paginationShadow}
            contentAfterTotalItems={
              pagination.showRecentItems && (
                <>
                  <RecentItems
                    showRecentItems={showRecentItems}
                    setShowRecentItems={handleRecentItems}
                  />
                </>
              )
            }
            dontShowActivePage={showRecentItems}
            onClickOnPageNumber={() => {
              if (showRecentItems) setShowRecentItems(false);
            }}
            disabled={pagination.disabled}
          />
        )}
      </>
    );
  }
);

export default AppTable;
